import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { NoteCard } from "@/components/NoteCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Sparkles, ShieldCheck, Zap, Loader2 } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const EXAMS = ["MBA", "CFA", "FRM", "CA", "M.Tech", "MSc", "LLM", "MD", "PMP", "AWS", "Data Science", "PhD Research"];

export default function Marketplace() {
  const [notes, setNotes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("all");
  const [level, setLevel] = useState("all");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [purchasedIds, setPurchasedIds] = useState([]);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notes", { params: { search, subject, level, sort } });
      const owned = new Set(purchasedIds);
      setNotes(data.map((n) => ({ ...n, owned: owned.has(n.id) })));
    } finally {
      setLoading(false);
    }
  }, [search, subject, level, sort, purchasedIds]);

  useEffect(() => {
    api.get("/notes/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    api.get("/notes/levels").then((r) => setLevels(r.data)).catch(() => {});
    if (localStorage.getItem("nb_token")) {
      api.get("/purchases/ids").then((r) => setPurchasedIds(r.data)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchNotes, 250);
    return () => clearTimeout(t);
  }, [fetchNotes]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100">
              <span className="flex gap-0.5">
                <span className="h-2 w-2 rounded-full bg-orange-500" />
                <span className="h-2 w-2 rounded-full bg-white border border-slate-200" />
                <span className="h-2 w-2 rounded-full bg-green-600" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-overline text-orange-700">
                India's advanced notes marketplace
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-slate-900">
              Master's, PhD & <span className="text-primary">pro-course</span> notes by India's experts.
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
              In-depth notes for MBA, CFA, MBBS/MD, M.Tech, LLM and PhD research — crafted by scholars,
              toppers and certified professionals. Buy several at once and study smarter.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <Feature icon={ShieldCheck} label="Secure payments" />
              <Feature icon={Zap} label="Instant PDF access" />
              <Feature icon={Sparkles} label="Topper-curated" />
            </div>
          </div>
          <div className="relative animate-fade-up">
            <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl" />
            <img
              src={HERO_IMG}
              alt="Indian students studying together"
              className="relative rounded-2xl shadow-xl object-cover w-full aspect-[4/3]"
            />
          </div>
        </div>

        {/* Trust stats */}
        <div className="border-t border-border bg-slate-50/60">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat value="500+" label="Study notes" />
            <Stat value="21" label="Subjects covered" />
            <Stat value="15+" label="Verified sellers" />
            <Stat value="100%" label="Instant download" />
          </div>
        </div>
      </section>

      {/* Exam categories */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-12">
        <p className="text-xs font-semibold uppercase tracking-overline text-muted-foreground mb-4">
          Popular exams & courses
        </p>
        <div className="flex flex-wrap gap-2.5">
          {EXAMS.map((ex) => (
            <button
              key={ex}
              onClick={() => setSearch(ex)}
              className="px-4 py-2 rounded-full text-sm font-medium border border-border bg-white text-slate-700 hover:border-primary hover:text-primary transition-colors"
              data-testid={`exam-chip-${ex}`}
            >
              {ex}
            </button>
          ))}
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-slate-900">
              Browse notes
            </h2>
            <p className="text-muted-foreground mt-1">Find the perfect notes for your subject.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="w-full sm:w-40" data-testid="subject-filter">
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjects.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="w-full sm:w-48" data-testid="level-filter">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                {levels.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-full sm:w-36" data-testid="sort-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Newest</SelectItem>
                <SelectItem value="price_low">Price: Low</SelectItem>
                <SelectItem value="price_high">Price: High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-24" data-testid="empty-state">
            <p className="text-lg font-medium text-slate-900">No notes found</p>
            <p className="text-muted-foreground mt-1">Be the first to sell notes in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="notes-grid">
            {notes.map((n, i) => (
              <NoteCard key={n.id} note={n} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const Feature = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
    <Icon className="h-5 w-5 text-primary" />
    {label}
  </div>
);

const Stat = ({ value, label }) => (
  <div>
    <p className="font-heading font-extrabold text-2xl md:text-3xl text-slate-900 tracking-tight">{value}</p>
    <p className="text-xs md:text-sm text-muted-foreground mt-0.5">{label}</p>
  </div>
);
